// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { messageAPI, fileAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Send, 
  Paperclip, 
  Loader2,
  MessageSquare,
  File,
  Image as ImageIcon,
  X
} from "lucide-react";
import { toast } from "sonner";
import { formatRelativeTime, getInitials } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatPage() {
  const { workspaceId } = useParams();
  const { user, canManage } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const loadMessages = useCallback(async (since = null) => {
    try {
      const response = await messageAPI.list(workspaceId, since);
      if (since) {
        setMessages(prev => [...prev, ...response.data.filter(m => !prev.find(p => p.id === m.id))]);
      } else {
        setMessages(response.data);
      }
    } catch (error) {
      console.error("Failed to load messages:", error);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadMessages();
    
    // Polling for new messages every 5 seconds
    pollIntervalRef.current = setInterval(() => {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage) {
        loadMessages(lastMessage.created_at);
      }
    }, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedFile) return;

    setSending(true);
    let fileUrl = null;

    try {
      // Upload file first if selected
      if (selectedFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("storage_type", "local");
        
        const uploadRes = await fileAPI.upload(workspaceId, formData);
        fileUrl = uploadRes.data.url;
        setUploading(false);
      }

      // Send message
      const response = await messageAPI.send(workspaceId, {
        content: newMessage.trim() || (selectedFile ? `Fichier: ${selectedFile.name}` : ""),
        file_url: fileUrl,
      });

      setMessages([...messages, response.data]);
      setNewMessage("");
      setSelectedFile(null);
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error("Le fichier est trop volumineux (max 50MB)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const getFilePreview = (fileUrl) => {
    if (!fileUrl) return null;
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl);
    
    if (isImage) {
      return (
        <img 
          src={`${BACKEND_URL}${fileUrl}`}
          alt="Shared file"
          className="max-w-xs max-h-48 rounded-lg mt-2"
        />
      );
    }
    
    return (
      <a 
        href={`${BACKEND_URL}${fileUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-muted/50 text-sm hover:bg-muted transition-colors"
      >
        <File className="h-4 w-4" />
        Télécharger le fichier
      </a>
    );
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="h-[calc(100vh-4rem)] flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4">
          <h1 className="text-xl font-semibold">Chat de l'espace</h1>
          <p className="text-sm text-muted-foreground">
            Communiquez avec les participants
          </p>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-6">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageSquare className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 font-semibold">Aucun message</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Soyez le premier à envoyer un message !
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) => {
                const isOwn = message.user_id === user?.id;
                return (
                  <div 
                    key={message.id}
                    className={`flex gap-3 ${isOwn ? "flex-row-reverse" : ""}`}
                    data-testid={`message-${index}`}
                  >
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={message.user_avatar} />
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {message.user_name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className={`max-w-[70%] ${isOwn ? "text-right" : ""}`}>
                      <div className={`flex items-center gap-2 mb-1 ${isOwn ? "flex-row-reverse" : ""}`}>
                        <span className="text-sm font-medium">{message.user_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(message.created_at)}
                        </span>
                      </div>
                      <div 
                        className={`rounded-lg px-4 py-2 ${
                          isOwn 
                            ? "bg-primary text-primary-foreground" 
                            : "bg-muted"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      </div>
                      {message.file_url && getFilePreview(message.file_url)}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="border-t p-4 bg-card">
          {selectedFile && (
            <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-muted">
              {selectedFile.type.startsWith('image/') ? (
                <ImageIcon className="h-4 w-4" />
              ) : (
                <File className="h-4 w-4" />
              )}
              <span className="text-sm flex-1 truncate">{selectedFile.name}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setSelectedFile(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          <form onSubmit={handleSend} className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              data-testid="attach-file-btn"
            >
              <Paperclip className="h-5 w-5" />
            </Button>
            <Input
              placeholder="Écrivez un message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={sending}
              className="flex-1"
              data-testid="message-input"
            />
            <Button 
              type="submit" 
              disabled={sending || (!newMessage.trim() && !selectedFile)}
              data-testid="send-message-btn"
            >
              {sending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </form>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
